import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useRouter } from 'vue-router'
import DBConfigService from '@/core/services/DBConfigService'
import { useAuthStore } from './auth'
import { debounce } from 'lodash-es'

/**
 * Interface for client list filter settings
 */
interface ClientListSettings {
    groups: string[]
    status: string[]
    locations: string[]
    scrollpositiontop: number
    searchquery: string
    currentPage: number
    /** Listenmodus der Kundenübersicht: aktive oder archivierte Kunden */
    archiveStatus: 'active' | 'archive'
    // New filter fields
    filterLetter: string
    filterCity: string
    filterYear: number | ''
    filterDateFrom: string
    filterDateTo: string
    filterHasActiveContract: boolean
    filterHasOpenCard: boolean
    filterMinWeeksSinceBooking: number | null
    /** Spaltensortierung der Kundenliste (Klick auf den Tabellenkopf) */
    sortColumn: 'name' | 'street' | 'city' | 'created' | 'lastbooking'
    sortAsc: boolean
}

/**
 * Interface for per-location room settings
 */
interface LocationSettings {
    activeroomid: string
}

/**
 * Interface for CashPoint view settings
 */
interface CashpointSettings {
    cardView: 'list' | 'grid'
    existingContractView: 'list' | 'grid'
    activeTab: 'cards' | 'products' | 'custom'
    cardServiceFilter: string
    cardEinheitenFilter: string
}

/**
 * Interface for Workouts list filter settings (/work/trainings/workouts)
 */
export interface WorkoutsSettings {
    kindFilter: string    // '' | 'interval' | 'cardio' | 'systematic'
    serviceFilter: string // free-text / tag filter
    search: string
    archive: number       // 0 = active, 1 = archived
}

/**
 * Interface for persisting the currently open modal across page refreshes.
 * Only modals listed in PERSISTABLE_MODALS in tables.ts are tracked.
 */
export interface ActiveModalSettings {
    name: string                    // Modal key, e.g. 'addcontract', 'editcontract'
    data: Record<string, any>       // Serialisierter Modal-Zustand (show/isOpen + Konfiguration)
}

/**
 * Interface for company-specific view settings
 * Stores all user preferences per company for persistence across sessions
 */
export interface CompanyViewSettings {
    // Navigation
    activeroute: string
    activelocation: string
    activeroom: string

    // Active client mode
    activeclient: string        // clients_mapping.id or '' when no client selected
    calenderviewmode: string    // 'studio' | 'client'

    // Calendar Settings
    calendarcellheight: number
    calendardaystoshow: number
    calendarselecteddays: string[]
    calendartimestepsinminutes: number
    calendarshowarchivedtrainings: boolean
    calendarshowresources: boolean
    calendarshowresourcesoverview: boolean
    calendarshowtodos: boolean
    calendarview: string // 'day', 'week', or 'month'
    calendarviewmode: string // 'planning' or 'weekcompact'
    calendarscrollposition: number // scrollTop in px, 0 = default (08:00)
    calendarscrolltime: number // oben sichtbare Uhrzeit in Minuten seit Mitternacht, -1 = nicht gesetzt
    calendardate: string // last active date 'YYYY-MM-DD'

    // Client List Settings
    clientlistsettings: ClientListSettings

    // Per-location settings
    locations: Record<string, LocationSettings[]>

    // CashPoint view settings
    cashpointsettings: CashpointSettings

    // Workouts list filter settings
    workoutssettings: WorkoutsSettings

    // Inbox
    lastInboxAccount: string

    // Mahnwesen
    mahnwesenPeriod: string // period key, e.g. 'y0', 'all', 'd30', etc.

    // Finance → Rechnungen
    invoicesSortBy: string // sort key, e.g. 'dateDesc', 'open', 'dateAsc', 'amountDesc'

    // Aktives Modal — wird beim Refresh wiederhergestellt
    activeModal: ActiveModalSettings | null
}

/**
 * Default settings factory
 * Creates a new settings object with sensible defaults
 */
function createDefaultSettings(): CompanyViewSettings {
    return {
        activeroute: '/calendar',
        activelocation: '',
        activeroom: '',
        activeclient: '',
        calenderviewmode: 'studio',
        calendarcellheight: 50,
        calendardaystoshow: 7,
        calendarselecteddays: [],
        calendartimestepsinminutes: 15,
        calendarshowarchivedtrainings: false,
        calendarshowresources: false,
        calendarshowresourcesoverview: false,
        calendarshowtodos: false,
        calendarview: 'week',
        calendarviewmode: 'planning',
        calendarscrollposition: -1, // -1 = not yet saved; will default to 08:00
        calendarscrolltime: -1, // -1 = noch nie gescrollt; Tag/Woche starten dann auf ihrem Default
        calendardate: '', // empty = use today on next load
        clientlistsettings: {
            groups: [],
            status: [],
            locations: [],
            scrollpositiontop: 0,
            searchquery: '',
            currentPage: 1,
            archiveStatus: 'active',
            filterLetter: '',
            filterCity: '',
            filterYear: '',
            filterDateFrom: '',
            filterDateTo: '',
            filterHasActiveContract: false,
            filterHasOpenCard: false,
            filterMinWeeksSinceBooking: null,
            sortColumn: 'name',
            sortAsc: true
        },
        locations: {},
        cashpointsettings: {
            cardView: 'list',
            existingContractView: 'list',
            activeTab: 'cards',
            cardServiceFilter: '',
            cardEinheitenFilter: '',
        },
        workoutssettings: {
            kindFilter: '',
            serviceFilter: '',
            search: '',
            archive: 0,
        },
        lastInboxAccount: '',
        mahnwesenPeriod: 'y0',
        invoicesSortBy: 'dateDesc',
        activeModal: null,
    }
}

/**
 * View Settings Store
 * Manages user-specific view preferences with automatic persistence to IndexedDB
 * Settings are organized per company to support multi-company users
 */
export const useViewSettingsStore = defineStore('viewSettings', () => {
    const router = useRouter()
    const authStore = useAuthStore()

    // State
    const activeSettingsPerView = ref<Record<string, CompanyViewSettings>>({})
    const lastActiveCompany = ref<string>('') // Track which company was last active
    const isInitialized = ref(false)
    const isSaving = ref(false)

    /**
     * Save current settings to IndexedDB
     * Debounced to prevent excessive writes
     */
    const saveToIndexedDB = debounce(async () => {
        if (!authStore.user?.id) {
            console.warn('Cannot save settings: user not authenticated')
            return
        }

        isSaving.value = true
        try {
            // Save both settings and lastActiveCompany
            const dataToSave = {
                settings: activeSettingsPerView.value,
                lastActiveCompany: lastActiveCompany.value
            }
            await DBConfigService.updateUserViewSettings(
                authStore.user.id,
                dataToSave
            )
            console.log('✅ View settings saved to IndexedDB', { lastActiveCompany: lastActiveCompany.value })
        } catch (error) {
            console.error('❌ Failed to save view settings:', error)
        } finally {
            isSaving.value = false
        }
    }, 300) // 300ms debounce

    /**
     * Initialize settings from IndexedDB
     * Called once on app startup after authentication
     */
    async function initializeSettings(userId: string): Promise<void> {
        if (isInitialized.value) {
            console.log('View settings already initialized')
            return
        }

        try {
            const savedData = await DBConfigService.getUserViewSettings(userId)
            console.log('📊 getUserViewSettings returned:', savedData)

            // Handle both old format (direct settings object) and new format (with lastActiveCompany)
            if (savedData && Object.keys(savedData).length > 0) {
                if (savedData.settings) {
                    // New format with lastActiveCompany
                    activeSettingsPerView.value = savedData.settings
                    lastActiveCompany.value = savedData.lastActiveCompany || ''
                    console.log('✅ View settings loaded from IndexedDB:', {
                        companies: Object.keys(savedData.settings),
                        lastActiveCompany: lastActiveCompany.value
                    })
                } else {
                    // Old format - direct settings object (backward compatibility)
                    activeSettingsPerView.value = savedData
                    lastActiveCompany.value = ''
                    console.log('✅ View settings loaded (old format):', Object.keys(savedData))
                }
            } else {
                console.log('ℹ️ No saved settings found, using defaults')
                activeSettingsPerView.value = {}
                lastActiveCompany.value = ''
            }

            isInitialized.value = true
        } catch (error) {
            console.error('❌ Failed to load view settings:', error)
            activeSettingsPerView.value = {}
            lastActiveCompany.value = ''
            isInitialized.value = true
        }
    }

    /**
     * Get settings for a specific company.
     * READ-ONLY: back-fills missing keys into the reactive object but never saves.
     * Saves happen only in the explicit update*() methods.
     */
    function getCompanySettings(companyId: string): CompanyViewSettings {
        if (!activeSettingsPerView.value[companyId]) {
            // First time this company is accessed — initialize with defaults.
            // Do NOT call saveToIndexedDB() here; the caller that modified data
            // (e.g. updateCalendarSettings) is responsible for persisting.
            activeSettingsPerView.value[companyId] = createDefaultSettings()
        } else {
            // Back-fill any keys added after the initial save (non-breaking migration).
            // Generic, so a newly added key can never again crash an old snapshot.
            const s = activeSettingsPerView.value[companyId]
            const defaults = createDefaultSettings()

            for (const key of Object.keys(defaults) as (keyof CompanyViewSettings)[]) {
                const def = defaults[key]
                const cur = s[key]

                if (cur === undefined || cur === null) {
                    // activeModal legitimately defaults to null — keep it null, don't
                    // overwrite an explicit null with anything else.
                    if (!(cur === null && def === null)) {
                        (s as any)[key] = def
                    }
                    continue
                }

                // Nested plain objects (clientlistsettings, cashpointsettings,
                // workoutssettings): back-fill their individual keys too.
                if (
                    def !== null && typeof def === 'object' && !Array.isArray(def) &&
                    typeof cur === 'object' && !Array.isArray(cur)
                ) {
                    for (const subKey of Object.keys(def)) {
                        if ((cur as any)[subKey] === undefined) {
                            (cur as any)[subKey] = (def as any)[subKey]
                        }
                    }
                }
            }
        }
        return activeSettingsPerView.value[companyId]
    }

    /**
     * Update calendar-specific settings for a company
     */
    function updateCalendarSettings(
        companyId: string,
        settings: Partial<Pick<CompanyViewSettings,
            'calendarcellheight' |
            'calendardaystoshow' |
            'calendarselecteddays' |
            'calendartimestepsinminutes' |
            'calendarshowarchivedtrainings' |
            'calendarshowresources' |
            'calendarshowresourcesoverview' |
            'calendarshowtodos' |
            'calendarview' |
            'calendarviewmode' |
            'calendarscrollposition' |
            'calendarscrolltime' |
            'calendardate'
        >>
    ): void {
        const companySettings = getCompanySettings(companyId)
        Object.assign(companySettings, settings)
        saveToIndexedDB()
    }

    /**
     * Persist which company is currently active.
     * This is the ONLY writer of lastActiveCompany — it must reflect a real,
     * current selection. Never derive it as a side effect of saving other
     * settings: during boot a transient/auto-picked company would otherwise be
     * written here and get "restored" on the next refresh.
     */
    function setLastActiveCompany(companyId: string): void {
        if (!companyId || lastActiveCompany.value === companyId) return
        lastActiveCompany.value = companyId
        // Make sure the company has a settings bucket so the save carries it
        getCompanySettings(companyId)
        saveToIndexedDB()
    }

    /**
     * Update active selections (location, room, route)
     */
    function updateActiveSelections(
        companyId: string,
        selections: Partial<Pick<CompanyViewSettings,
            'activelocation' |
            'activeroom' |
            'activeroute'
        >>
    ): void {
        const companySettings = getCompanySettings(companyId)
        Object.assign(companySettings, selections)
        saveToIndexedDB()
    }

    /**
     * Set or clear the active client for client mode.
     * Persists both activeclient and calenderviewmode to IndexedDB.
     */
    function updateActiveClient(
        companyId: string,
        clientId: string,
        viewMode: 'studio' | 'client' = clientId ? 'client' : 'studio'
    ): void {
        const companySettings = getCompanySettings(companyId)
        // Back-fill if loaded from old IndexedDB snapshot
        if (companySettings.activeclient === undefined) companySettings.activeclient = ''
        if (companySettings.calenderviewmode === undefined) companySettings.calenderviewmode = 'studio'
        companySettings.activeclient = clientId
        companySettings.calenderviewmode = viewMode
        saveToIndexedDB()
    }

    /**
     * Update client list filter settings
     */
    function updateClientListSettings(
        companyId: string,
        settings: Partial<ClientListSettings>
    ): void {
        const companySettings = getCompanySettings(companyId)
        // Guard: back-fill if loaded from old IndexedDB snapshot without this key
        if (!companySettings.clientlistsettings) {
            companySettings.clientlistsettings = createDefaultSettings().clientlistsettings
        }
        Object.assign(companySettings.clientlistsettings, settings)
        saveToIndexedDB()
    }

    /**
     * Update CashPoint view settings
     */
    function updateCashpointSettings(
        companyId: string,
        settings: Partial<CashpointSettings>
    ): void {
        const companySettings = getCompanySettings(companyId)
        // Guard: back-fill if loaded from old IndexedDB snapshot without this key
        if (!companySettings.cashpointsettings) {
            companySettings.cashpointsettings = createDefaultSettings().cashpointsettings
        }
        Object.assign(companySettings.cashpointsettings, settings)
        saveToIndexedDB()
    }

    /**
     * Update last selected inbox account
     */
    function updateInboxAccount(companyId: string, accountId: string): void {
        const companySettings = getCompanySettings(companyId)
        companySettings.lastInboxAccount = accountId
        saveToIndexedDB()
    }

    /**
     * Persist the currently open modal so it can be restored after a page refresh.
     * Pass null to clear (modal was closed).
     */
    function updateModalSettings(companyId: string, modal: ActiveModalSettings | null): void {
        const companySettings = getCompanySettings(companyId)
        companySettings.activeModal = modal
        saveToIndexedDB()
    }

    /**
     * Update Mahnwesen filter settings
     */
    function updateMahnwesenSettings(companyId: string, period: string): void {
        const companySettings = getCompanySettings(companyId)
        companySettings.mahnwesenPeriod = period
        saveToIndexedDB()
    }

    /**
     * Update sort order of the finance invoice list
     */
    function updateInvoicesSettings(companyId: string, sortBy: string): void {
        const companySettings = getCompanySettings(companyId)
        companySettings.invoicesSortBy = sortBy
        saveToIndexedDB()
    }

    /**
     * Update Workouts list filter settings
     */
    function updateWorkoutsSettings(
        companyId: string,
        settings: Partial<WorkoutsSettings>
    ): void {
        const companySettings = getCompanySettings(companyId)
        if (!companySettings.workoutssettings) {
            companySettings.workoutssettings = createDefaultSettings().workoutssettings
        }
        Object.assign(companySettings.workoutssettings, settings)
        saveToIndexedDB()
    }

    /**
     * Update active room for a specific location
     */
    function updateLocationRoom(
        companyId: string,
        locationId: string,
        roomId: string
    ): void {
        const companySettings = getCompanySettings(companyId)
        if (!companySettings.locations[locationId]) {
            companySettings.locations[locationId] = []
        }
        companySettings.locations[locationId] = [{ activeroomid: roomId }]
        saveToIndexedDB()
    }

    /**
     * Restore settings for a company
     * Used when switching between companies
     */
    function restoreCompanySettings(companyId: string) {
        const settings = getCompanySettings(companyId)

        // Navigate to last route if auto-navigation is enabled
        if (settings.activeroute && router.currentRoute.value.path !== settings.activeroute) {
            router.push(settings.activeroute)
        }

        return settings
    }

    /**
     * Reset all settings (for testing or user request)
     */
    function resetAllSettings(): void {
        activeSettingsPerView.value = {}
        saveToIndexedDB()
    }

    /**
     * Reset settings for a specific company
     */
    function resetCompanySettings(companyId: string): void {
        if (activeSettingsPerView.value[companyId]) {
            activeSettingsPerView.value[companyId] = createDefaultSettings()
            saveToIndexedDB()
        }
    }

    // Computed
    const hasUnsavedChanges = computed(() => isSaving.value)

    return {
        // State
        activeSettingsPerView,
        lastActiveCompany, // Export so auth can restore the correct company
        isInitialized,
        hasUnsavedChanges,

        // Actions
        initializeSettings,
        setLastActiveCompany,
        getCompanySettings,
        updateCalendarSettings,
        updateActiveSelections,
        updateActiveClient,
        updateClientListSettings,
        updateCashpointSettings,
        updateInboxAccount,
        updateModalSettings,
        updateMahnwesenSettings,
        updateInvoicesSettings,
        updateWorkoutsSettings,
        updateLocationRoom,
        restoreCompanySettings,
        resetAllSettings,
        resetCompanySettings,
        saveToIndexedDB
    }
})
